import { Download } from "lucide-react";
import PropTypes from "prop-types";
import LoadingButton from "@components/ui-components/loading-button";
import Modal from "@components/ui-components/modal";
import { formatDate } from "@helpers/date";
import DocumentPreview from "./document-preview";
import { pdfFileName } from "../misc/helpers";
import {
    ACKNOWLEDGED_NOTE,
    ACKNOWLEDGEMENT_LABEL,
    FRAME_NOTE,
    FRAME_NOTE_LINK,
    PREVIEW_KIND,
    RETENTION_NOTE,
    VIEWER_STATUS
} from "../misc/constants";

// The contract ops sent, with the acknowledgement that records the brand's or
// publisher's signature on it.
//
// Two rules hold this together, and both are deliberate:
//   - the checkbox stays locked until the contract has actually been put in
//     front of the recipient — displayed by the browser's own reader in the
//     frame, or opened in their own reader where it cannot be embedded at all
//     — so a recorded acknowledgement always corresponds to a document that
//     was shown; and
//   - Download is never gated, because whoever acknowledges must be able to
//     keep their own copy of what they acknowledged.
const ContractReviewModal = ({
    open,
    contract,
    viewer,
    subtitle = "",
    accepting,
    acknowledged,
    canAcknowledge,
    hint,
    readOnly,
    onAcknowledgeChange,
    onOpenExternally,
    onAcknowledge,
    onClose
}) => {
    // The modal is driven off a contract that can disappear under it on a
    // refetch.
    if (!open || !contract?.exists) return null;

    // The frame's own trouble-reading hint only applies while the frame is
    // actually what's on screen; otherwise the download row below already
    // covers it, and the two would otherwise stack into a second row.
    const isFrameView = contract.previewKind !== PREVIEW_KIND.NONE
        && contract.previewKind !== PREVIEW_KIND.DOC
        && viewer.status !== VIEWER_STATUS.ERROR;

    const heading = [
        subtitle,
        contract.version === null ? null : `Version ${contract.version}`,
        readOnly && contract.signedAt ? `Signed on ${formatDate(contract.signedAt)}` : null
    ].filter(Boolean).join(" · ");

    return (
        <Modal
            open={open}
            onClose={onClose}
            width="xl"
            title={contract.name}
            subtitle={heading}
            footer={readOnly ? (
                <LoadingButton
                    color="neutral"
                    text="Close"
                    onClick={onClose}
                    testId="close-contract-review" />
            ) : (
                <>
                    <LoadingButton
                        color="neutral"
                        text="Cancel"
                        onClick={onClose}
                        disabled={accepting}
                        testId="cancel-contract-review" />
                    <LoadingButton
                        color="dark"
                        text="Sign contract"
                        loadingText="Signing…"
                        loading={accepting}
                        disabled={!acknowledged || !canAcknowledge}
                        onClick={onAcknowledge}
                        testId="accept-contract" />
                </>
            )}>
            <DocumentPreview
                contract={contract}
                viewer={viewer}
                onOpenExternally={onOpenExternally} />

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-muted">
                    {isFrameView ? (
                        <>
                            {FRAME_NOTE}{" "}
                            <a
                                href={contract.url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={onOpenExternally}
                                className="text-primary hover:underline"
                                data-cy="open-contract-new-tab">
                                {FRAME_NOTE_LINK}
                            </a>
                        </>
                    ) : RETENTION_NOTE}
                </p>
                <LoadingButton
                    color="neutral"
                    size="sm"
                    icon={Download}
                    text="Download copy"
                    href={contract.url}
                    download={pdfFileName(contract.name)}
                    disabled={!contract.url}
                    onClick={onOpenExternally}
                    testId="download-contract-copy" />
            </div>

            {readOnly ? (
                <p className="mt-4 text-sm text-muted">{ACKNOWLEDGED_NOTE}</p>
            ) : (
                <>
                    <label className={`mt-4 flex items-start gap-3 rounded-lg border p-4 ${
                        canAcknowledge
                            ? "cursor-pointer border-gray-200 bg-light/40"
                            : "cursor-not-allowed border-gray-200 bg-light/20 opacity-60"
                    }`}>
                        <input
                            type="checkbox"
                            checked={acknowledged}
                            onChange={(event) => onAcknowledgeChange(event.target.checked)}
                            disabled={!canAcknowledge || accepting}
                            data-cy="acknowledge-contract"
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary" />
                        <span className="text-sm text-gray-900">{ACKNOWLEDGEMENT_LABEL}</span>
                    </label>

                    {hint && (
                        <p className="mt-2 text-xs text-muted" role="status" data-cy="acknowledge-hint">{hint}</p>
                    )}
                </>
            )}
        </Modal>
    );
};

ContractReviewModal.propTypes = {
    open               : PropTypes.bool.isRequired,
    contract           : PropTypes.object,
    viewer             : PropTypes.object.isRequired,
    subtitle           : PropTypes.string,
    accepting          : PropTypes.bool,
    acknowledged       : PropTypes.bool,
    canAcknowledge     : PropTypes.bool,
    hint               : PropTypes.string,
    readOnly           : PropTypes.bool,
    onAcknowledgeChange: PropTypes.func.isRequired,
    onOpenExternally   : PropTypes.func.isRequired,
    onAcknowledge      : PropTypes.func.isRequired,
    onClose            : PropTypes.func.isRequired
};

export default ContractReviewModal;
